import { AudioPlayer } from '@components/AudioPlayer';
import { DropZone } from '@components/Files/DropZone';
import { FilePreview } from '@components/Files/FilePreview';
import { UploadProvider, useUploadContext } from '@components/Files/UploadProvider';
import { RepositoryS3Uploader } from '@components/Files/UploadProvider/providers/RepositoryS3Uploader';
import { isRepositoryAudioFile } from '@pages/User/RepositoryPage/helpers/isRepositoryAudioFile';
import { mapRepositoryAttachmentsToFiles } from '@pages/User/RepositoryPage/helpers/mapRepositoryAttachmentsToFiles';
import { resolveRepositoryAttachmentId } from '@pages/User/RepositoryPage/helpers/resolveRepositoryAttachmentId';
import { useAPI } from '@providers/useAPI';
import { useMutation, useQuery } from '@tanstack/react-query';
import { CreateAttachmentBodyDto, RepositoriesService } from 'app/API';
import { useNotifyQueryError } from 'app/hooks/useNotifyQueryError';
import { ApiCallError } from 'app/http/ApiCallError';
import { notifyApiCallError } from 'app/http/notifyApiCallError';
import { repositoriesKeys } from 'app/queries/repositories/repositoriesKeys';
import React, { useCallback, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { ItemInterface, ReactSortable } from 'react-sortablejs';
import { createFetchRepositoryAttachmentPlaybackUrlQueryFn } from '../../api/fetchRepositoryAttachmentPlaybackUrl';
import { useDownloadRepositoryAttachment } from './hooks/useDownloadRepositoryAttachment';
import type { TProps } from './types/TProps';

const RepositoryFilePlayback: React.FC<{ attachmentId: string; fileName: string }> = ({ attachmentId, fileName }) => {
  const { call } = useAPI();
  const playbackUrlQuery = useQuery({
    queryKey: repositoriesKeys.attachmentPlaybackUrl(attachmentId),
    queryFn: createFetchRepositoryAttachmentPlaybackUrlQueryFn(call),
    staleTime: Infinity,
    gcTime: 0,
  });
  const playbackUrl = playbackUrlQuery.data?.url || null;
  useNotifyQueryError(playbackUrlQuery.error);
  const downloadAttachment = useDownloadRepositoryAttachment();

  if (!playbackUrl) {
    return null;
  }
  return (
    <AudioPlayer
      src={playbackUrl}
      downloadFileName={fileName}
      onDownload={() => downloadAttachment(attachmentId, fileName)}
    />
  );
};

const RepositoryFilesBlockInner: React.FC<{
  accept: string;
  organizationSlug: string;
  attachments: TProps['attachments'];
  allowPlayback?: boolean;
  onUploadingChange: (isUploading: boolean) => void;
  onAttachmentRemoved?: (attachmentId: string) => void;
  repositoryIdRef: React.MutableRefObject<string>;
}> = ({
  accept,
  organizationSlug,
  attachments,
  allowPlayback,
  onUploadingChange,
  onAttachmentRemoved,
  repositoryIdRef,
}) => {
  const { call } = useAPI();
  const { files, attachExisting, setExtra, updateMeta, reorderFiles } = useUploadContext();
  const removedAttachmentIdsRef = useRef(new Set<string>());
  const downloadAttachment = useDownloadRepositoryAttachment();

  const { mutateAsync: registerAttachment } = useMutation({
    mutationFn: (requestBody: CreateAttachmentBodyDto) =>
      call(() => RepositoriesService.createAttachments({ repositoryId: requestBody.repositoryId, requestBody })),
  });

  const { mutateAsync: removeAttachment } = useMutation({
    mutationFn: async (attachmentId: string) => {
      const response = await call(() => RepositoriesService.removeAttachment({ repositoryAttachmentId: attachmentId }));
      if (!response.success) {
        throw new Error(response.error || 'Unable to remove the file.');
      }
    },
    onError: (error) => (error instanceof ApiCallError ? notifyApiCallError(error) : toast.error(error.message)),
  });

  useEffect(() => {
    const existingIds = new Set(
      files
        .filter((file) => file.meta.status !== 'removed')
        .filter((file) => file.meta.status === 'external' || file.meta.extra?.repositoryAttachmentId)
        .map((file) => resolveRepositoryAttachmentId(file.meta.extra?.repositoryAttachmentId) || file.meta.id),
    );
    const notAttachedYet = mapRepositoryAttachmentsToFiles(
      attachments.filter(
        (attachment) => !existingIds.has(attachment.id) && !removedAttachmentIdsRef.current.has(attachment.id),
      ),
    );
    if (notAttachedYet.length) {
      attachExisting(notAttachedYet);
    }
  }, [attachments, files, attachExisting]);

  useEffect(() => {
    const activeFiles = files.filter((f) => f.meta.status !== 'removed');
    const isRegisteredOnServer = (file: (typeof activeFiles)[0]) =>
      Boolean(
        file.meta.extra?.repositoryRegistered || resolveRepositoryAttachmentId(file.meta.extra?.repositoryAttachmentId),
      );
    const hasActiveUpload = activeFiles.some(
      (f) => f.meta.status === 'uploading' || f.meta.status === 'preparing' || f.meta.status === 'queued',
    );
    const hasPendingRegistration = activeFiles.some(
      (f) => f.meta.status === 'done' && Boolean(f.file) && !isRegisteredOnServer(f),
    );
    onUploadingChange(hasActiveUpload || hasPendingRegistration);
  }, [files, onUploadingChange]);

  useEffect(() => {
    return () => {
      onUploadingChange(false);
    };
  }, [onUploadingChange]);

  useEffect(() => {
    const activeFiles = files.filter((f) => f.meta.status !== 'removed');
    files.forEach((f) => {
      if (
        f.meta.status !== 'done' ||
        !f.file ||
        f.meta.extra?.repositoryRegistered ||
        f.meta.extra?.repositoryRegistering
      ) {
        return;
      }

      setExtra(f.meta.id, { repositoryRegistering: true });
      const weight = activeFiles.findIndex((other) => other.meta.id === f.meta.id);
      const isAudio = isRepositoryAudioFile(f.meta.name);

      registerAttachment({
        repositoryId: repositoryIdRef.current,
        fileName: f.meta.name,
        fileSize: String(f.file?.size ?? f.meta.size ?? '0'),
        weight,
        isAudio,
        audioDuration: Number(f.meta.extra?.duration ?? 0),
        organizationSlug,
      })
        .then((response) => {
          const attachmentId = resolveRepositoryAttachmentId(response);
          if (!attachmentId) {
            updateMeta(f.meta.id, {
              status: 'error',
              errorMessage: 'Failed to register file on the server',
            });
            setExtra(f.meta.id, { repositoryRegistering: false });
            return;
          }
          setExtra(f.meta.id, {
            repositoryRegistered: true,
            repositoryRegistering: false,
            repositoryAttachmentId: attachmentId,
          });
        })
        .catch(() => {
          updateMeta(f.meta.id, {
            status: 'error',
            errorMessage: 'Failed to register file on the server',
          });
          setExtra(f.meta.id, { repositoryRegistering: false });
        });
    });
  }, [files, organizationSlug, registerAttachment, setExtra, updateMeta, repositoryIdRef]);

  const handleDownload = useCallback(
    async (meta: { name?: string; extra?: Record<string, unknown> }) => {
      const attachmentId = resolveRepositoryAttachmentId(meta.extra?.repositoryAttachmentId);
      if (!attachmentId) {
        return;
      }
      await downloadAttachment(attachmentId, meta.name || 'download');
    },
    [downloadAttachment],
  );

  const handleRemoveRemote = useCallback(
    async (meta: { name?: string; extra?: Record<string, unknown> }) => {
      const attachmentId = resolveRepositoryAttachmentId(meta.extra?.repositoryAttachmentId);
      if (!attachmentId) {
        return;
      }
      await removeAttachment(attachmentId);
      removedAttachmentIdsRef.current.add(attachmentId);
      onAttachmentRemoved?.(attachmentId);
    },
    [removeAttachment, onAttachmentRemoved],
  );

  const visibleFiles = files.filter((item) => item.meta.status !== 'removed');
  const sortableList: ItemInterface[] = visibleFiles.map((file) => ({ id: file.meta.id }));
  const setSortableList = (list: ItemInterface[]) => {
    reorderFiles(list.map((item) => String(item.id)));
  };

  const renderFilePreview = (item: (typeof visibleFiles)[0]) => {
    const attachmentId = resolveRepositoryAttachmentId(item.meta.extra?.repositoryAttachmentId) ?? '';
    const canDownload = Boolean(attachmentId);
    const erroredState = item.meta.status === 'error';
    const processingState =
      item.meta.status === 'preparing' ||
      item.meta.status === 'uploading' ||
      Boolean(item.meta.extra?.repositoryRegistering);
    const allowRemoveFile = !processingState || erroredState;
    const isTrmFile = item.meta.name.toLowerCase().endsWith('.trm');
    const showPlayback = allowPlayback && canDownload && !isTrmFile;

    return (
      <FilePreview
        item={item}
        allowRemove={allowRemoveFile}
        allowRetry
        allowDownload={canDownload && !showPlayback}
        onDownload={handleDownload}
        onRemoveRemote={handleRemoveRemote}
        bottomSlot={
          showPlayback ? <RepositoryFilePlayback attachmentId={attachmentId} fileName={item.meta.name} /> : undefined
        }
      />
    );
  };

  return (
    <div>
      <DropZone accept={accept} multiple={true} />

      {(visibleFiles.length < 2 ? visibleFiles : []).map((item) => (
        <div key={item.meta.id} className="mt-2">
          {renderFilePreview(item)}
        </div>
      ))}
      {visibleFiles.length >= 2 ? (
        <ReactSortable list={sortableList} setList={setSortableList} animation={200} delay={1}>
          {visibleFiles.map((item) => (
            <div key={item.meta.id} className="mt-2">
              {renderFilePreview(item)}
            </div>
          ))}
        </ReactSortable>
      ) : null}
    </div>
  );
};

export const RepositoryFilesBlock: React.FC<TProps> = ({
  repositoryId,
  organizationSlug,
  accept,
  attachments,
  allowPlayback,
  onUploadingChange,
  onAttachmentRemoved,
}) => {
  const { call } = useAPI();
  const repositoryIdRef = useRef(repositoryId);
  repositoryIdRef.current = repositoryId;

  return (
    <UploadProvider
      config={{
        mode: 'immediate',
        uploader: RepositoryS3Uploader(call, () => repositoryIdRef.current),
        extra: { repositoryId: repositoryIdRef.current },
      }}
    >
      <RepositoryFilesBlockInner
        accept={accept}
        organizationSlug={organizationSlug}
        attachments={attachments}
        allowPlayback={allowPlayback}
        onUploadingChange={onUploadingChange}
        onAttachmentRemoved={onAttachmentRemoved}
        repositoryIdRef={repositoryIdRef}
      />
    </UploadProvider>
  );
};
